"use client";

import { useState } from "react";

import Link from "next/link";

import { OptimizedProductImage } from "@/components/product/OptimizedProductImage";
import SaveButton from "@/components/ui/SaveButton";
import { useSavedProducts } from "@/contexts/SavedProductsContext";
import { buildCanonicalProductPathFromRecord } from "@/lib/product-url";
import {
  type Product,
  getFormattedPrice,
  getProductAnyId,
  getProductImage,
  getProductImages,
  getProductName,
  getSavedProductId,
} from "@/lib/product-utils";

const EXTREME_WIDE_IMAGE_RATIO_THRESHOLD = 1.35;
const EXTREME_TALL_IMAGE_RATIO_THRESHOLD = 0.6;

export type RelatedProductCardVariant = "compact" | "editorial";

interface RelatedProductProps {
  product: Product;
  onClick?: () => void;
  variant?: RelatedProductCardVariant;
}

export function RelatedProductCard({ product, onClick, variant = "compact" }: RelatedProductProps) {
  const isEditorial = variant === "editorial";
  const [isHovered, setIsHovered] = useState(false);
  const [hoverImageLoaded, setHoverImageLoaded] = useState(false);
  const [primaryImageRatio, setPrimaryImageRatio] = useState<number | null>(null);
  const [hoverImageRatio, setHoverImageRatio] = useState<number | null>(null);
  const { isSaved } = useSavedProducts();

  const productName = getProductName(product);
  const productImage = getProductImage(product);
  const allImages = getProductImages(product);
  const hoverImage = allImages.length > 1 ? allImages[1] : productImage;
  const hasSecondaryHoverImage = allImages.length > 1 && hoverImage !== productImage;
  const price = getFormattedPrice(product);
  const brandName =
    product.enrichment?.canonical_brand?.brand_name || product.brand_name || product.catalog || "";
  const savedProductId = getSavedProductId(product, isSaved);
  const relatedProductId = getProductAnyId(product);
  const relatedProductHref = buildCanonicalProductPathFromRecord(
    product as unknown as Record<string, unknown>,
    relatedProductId
  );
  const wrapperClassName = `group ${onClick ? "cursor-pointer" : ""}`;

  const isExtremeRatio = (ratio: number | null): boolean =>
    ratio !== null &&
    Number.isFinite(ratio) &&
    (ratio > EXTREME_WIDE_IMAGE_RATIO_THRESHOLD || ratio < EXTREME_TALL_IMAGE_RATIO_THRESHOLD);
  const useContainPrimary = isExtremeRatio(primaryImageRatio);
  const useContainHover = isExtremeRatio(hoverImageRatio);
  const isHoverImageActive = hasSecondaryHoverImage && isHovered && hoverImageLoaded;
  const useContainedPresentation = isHoverImageActive ? useContainHover : useContainPrimary;
  const shouldRenderHoverImage = isEditorial ? hasSecondaryHoverImage : true;
  const cardContent = (
    <>
      <div
        className={
          isEditorial
            ? `relative aspect-[3/4] overflow-hidden ${useContainedPresentation ? "bg-white" : "bg-gray-50"}`
            : "relative mb-4 aspect-[4/5] overflow-visible bg-gray-100"
        }
      >
        <OptimizedProductImage
          src={productImage}
          alt={productName}
          fill
          sizes="(max-width: 767px) 50vw, (max-width: 1279px) 25vw, 20vw"
          className={
            isEditorial
              ? `transition-[opacity,transform] duration-500 ${
                  hasSecondaryHoverImage
                    ? isHovered && hoverImageLoaded
                      ? "opacity-0"
                      : "opacity-100"
                    : "opacity-100 group-hover:scale-105"
                } ${useContainPrimary ? "object-contain p-3" : "object-cover"}`
              : `h-full w-full object-cover transition-opacity duration-300 ${
                  isHovered && hoverImageLoaded ? "opacity-0" : "opacity-100"
                }`
          }
          onLoad={
            isEditorial
              ? (event) => {
                  const { naturalWidth, naturalHeight } = event.currentTarget;
                  if (!naturalWidth || !naturalHeight) return;
                  const ratio = naturalWidth / naturalHeight;
                  if (Number.isFinite(ratio)) {
                    setPrimaryImageRatio(ratio);
                  }
                }
              : undefined
          }
          unoptimized
        />
        {shouldRenderHoverImage && (
          <OptimizedProductImage
            src={hoverImage}
            alt={`${productName} alternate view`}
            fill
            sizes="(max-width: 767px) 50vw, (max-width: 1279px) 25vw, 20vw"
            className={
              isEditorial
                ? `transition-[opacity,transform] duration-500 ${
                    isHovered && hoverImageLoaded
                      ? "opacity-100 group-hover:scale-105"
                      : "opacity-0"
                  } ${useContainHover ? "object-contain p-3" : "object-cover"}`
                : `absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
                    isHovered && hoverImageLoaded ? "opacity-100" : "opacity-0"
                  }`
            }
            onLoad={(event) => {
              if (isEditorial) {
                const { naturalWidth, naturalHeight } = event.currentTarget;
                if (naturalWidth && naturalHeight) {
                  const ratio = naturalWidth / naturalHeight;
                  if (Number.isFinite(ratio)) {
                    setHoverImageRatio(ratio);
                  }
                }
              }
              setHoverImageLoaded(true);
            }}
            unoptimized
          />
        )}
        {!isEditorial && (
          <div className="hidden lg:block">
            <div
              className={`absolute right-4 top-4 space-y-2 transition-opacity duration-200 ${
                isHovered ? "opacity-100" : "opacity-0"
              }`}
            >
              <SaveButton
                productId={savedProductId}
                product={product}
                variant="icon"
                className="!h-8 !w-8 bg-white/90 !p-0 shadow-sm hover:bg-white"
              />
            </div>
          </div>
        )}
      </div>
      {isEditorial ? (
        <div className="flex items-baseline justify-between gap-4 px-4 py-4">
          <div className="min-w-0">
            {brandName && (
              <p className="text-xs font-normal uppercase tracking-[0.1em] text-gray-400">
                {brandName}
              </p>
            )}
            <h4 className="line-clamp-1 text-sm font-normal text-gray-900">{productName}</h4>
          </div>
          <span className="shrink-0 text-sm font-normal text-gray-900">{price.final}</span>
        </div>
      ) : (
        <div className="space-y-1">
          <p className="text-xs font-normal uppercase tracking-[0.1em] text-gray-500">
            {brandName}
          </p>
          <h4 className="line-clamp-2 text-sm font-medium leading-tight text-gray-900">
            {productName}
          </h4>
          <p className="text-sm font-normal text-gray-900">{price.final}</p>
        </div>
      )}
    </>
  );

  if (isEditorial && onClick) {
    if (!relatedProductHref) {
      return (
        <div
          className={wrapperClassName}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={onClick}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClick();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={productName}
        >
          {cardContent}
        </div>
      );
    }
    return (
      <Link
        href={relatedProductHref}
        className={wrapperClassName}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={(event) => {
          event.preventDefault();
          onClick();
        }}
        aria-label={productName}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <div
      className={wrapperClassName}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!onClick) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? productName : undefined}
    >
      {cardContent}
    </div>
  );
}
